import { useState } from "react";
import { Copy, Eye, EyeOff, Check, Lock } from "lucide-react";

/**
 * Aureon premium virtual card — luxury-grade visual treatment per tier.
 * Uses layered SVG patterns, EMV chip, contactless icon, and holographic band.
 */

// Tier-specific palettes (background gradient, secondary hue, foil accent, border, chip)
const TIER_STYLES = {
  100: {
    name: "Bronze",
    // rich cognac / bronze
    bg: `radial-gradient(ellipse at 20% 0%, #6B4426 0%, transparent 55%),
         radial-gradient(ellipse at 100% 100%, #241812 0%, transparent 60%),
         linear-gradient(135deg, #2B1A10 0%, #4E301C 45%, #1B120B 100%)`,
    foil: "#C99060",
    ring: "rgba(201, 144, 96, 0.35)",
    chipBase: "linear-gradient(135deg, #C99060 0%, #7A4E2D 100%)",
    text: "#F5E5D5",
    subtitle: "BRONZE · RESERVE",
    pattern: "guilloche",
    holo: "linear-gradient(115deg, transparent 40%, rgba(255,220,180,0.10) 50%, transparent 60%)",
  },
  250: {
    name: "Silver",
    // brushed platinum
    bg: `radial-gradient(ellipse at 30% 0%, #6E7175 0%, transparent 60%),
         radial-gradient(ellipse at 100% 100%, #0F1113 0%, transparent 55%),
         linear-gradient(135deg, #2A2D30 0%, #4D5257 50%, #1A1C1E 100%)`,
    foil: "#D9DFE4",
    ring: "rgba(217, 223, 228, 0.35)",
    chipBase: "linear-gradient(135deg, #E5EAEF 0%, #7F8489 100%)",
    text: "#F4F6F8",
    subtitle: "SILVER · RESERVE",
    pattern: "brushed",
    holo: "linear-gradient(115deg, transparent 42%, rgba(240,244,247,0.12) 50%, transparent 58%)",
  },
  500: {
    name: "Gold",
    // warm 24k gold
    bg: `radial-gradient(ellipse at 20% 0%, #A57A1B 0%, transparent 55%),
         radial-gradient(ellipse at 100% 100%, #1D1408 0%, transparent 55%),
         linear-gradient(135deg, #3A2A0F 0%, #7C5A17 45%, #221909 100%)`,
    foil: "#F0CE6A",
    ring: "rgba(240, 206, 106, 0.45)",
    chipBase: "linear-gradient(135deg, #FFE58A 0%, #A47D18 100%)",
    text: "#F5E9C3",
    subtitle: "GOLD · RESERVE",
    pattern: "guilloche",
    holo: "linear-gradient(115deg, transparent 40%, rgba(255,232,150,0.14) 50%, transparent 60%)",
  },
  1000: {
    name: "Black",
    // deep obsidian with gold accent
    bg: `radial-gradient(ellipse at 15% 0%, #1B1B1B 0%, transparent 60%),
         radial-gradient(ellipse at 100% 100%, #050505 0%, transparent 55%),
         linear-gradient(135deg, #0B0B0B 0%, #1A1A1A 50%, #030303 100%)`,
    foil: "#E5C158",
    ring: "rgba(229, 193, 88, 0.42)",
    chipBase: "linear-gradient(135deg, #F2D883 0%, #8A6810 100%)",
    text: "#EDEDED",
    subtitle: "BLACK · RESERVE",
    pattern: "carbon",
    holo: "linear-gradient(115deg, transparent 40%, rgba(229,193,88,0.10) 50%, transparent 60%)",
  },
  2000: {
    name: "Platinum",
    // iced platinum with cool silver shimmer
    bg: `radial-gradient(ellipse at 20% 0%, #D9D9D9 0%, transparent 55%),
         radial-gradient(ellipse at 100% 100%, #2A2E33 0%, transparent 55%),
         linear-gradient(135deg, #3B3F44 0%, #9FA3A8 48%, #22262A 100%)`,
    foil: "#F2F2F2",
    ring: "rgba(242, 242, 242, 0.45)",
    chipBase: "linear-gradient(135deg, #FFFFFF 0%, #8C9196 100%)",
    text: "#FAFBFC",
    subtitle: "PLATINUM · RESERVE",
    pattern: "brushed",
    holo: "linear-gradient(115deg, transparent 42%, rgba(255,255,255,0.18) 50%, transparent 58%)",
  },
  3000: {
    name: "Sapphire",
    // deep royal sapphire with cobalt flare
    bg: `radial-gradient(ellipse at 20% 0%, #1E66D0 0%, transparent 55%),
         radial-gradient(ellipse at 100% 100%, #050E22 0%, transparent 55%),
         linear-gradient(135deg, #0A1A3C 0%, #0F52BA 45%, #050E22 100%)`,
    foil: "#8BB8FF",
    ring: "rgba(139, 184, 255, 0.42)",
    chipBase: "linear-gradient(135deg, #BFD4FF 0%, #1E3F8C 100%)",
    text: "#EAF1FF",
    subtitle: "SAPPHIRE · RESERVE",
    pattern: "guilloche",
    holo: "linear-gradient(115deg, transparent 40%, rgba(139,184,255,0.14) 50%, transparent 60%)",
  },
  4000: {
    name: "Ruby",
    // crimson ruby with rose foil
    bg: `radial-gradient(ellipse at 20% 0%, #B50E4C 0%, transparent 55%),
         radial-gradient(ellipse at 100% 100%, #1C0410 0%, transparent 55%),
         linear-gradient(135deg, #3A0820 0%, #9C0C44 45%, #1C0410 100%)`,
    foil: "#F58AAE",
    ring: "rgba(245, 138, 174, 0.42)",
    chipBase: "linear-gradient(135deg, #FFBDD1 0%, #8C0E3C 100%)",
    text: "#FDE6EE",
    subtitle: "RUBY · RESERVE",
    pattern: "guilloche",
    holo: "linear-gradient(115deg, transparent 40%, rgba(245,138,174,0.14) 50%, transparent 60%)",
  },
  5000: {
    name: "Diamond",
    // iced diamond — pale cyan over graphite
    bg: `radial-gradient(ellipse at 20% 0%, #B9F2FF 0%, transparent 50%),
         radial-gradient(ellipse at 100% 100%, #0A1418 0%, transparent 55%),
         linear-gradient(135deg, #122026 0%, #4F7A8C 48%, #0A1418 100%)`,
    foil: "#E7FBFF",
    ring: "rgba(231, 251, 255, 0.5)",
    chipBase: "linear-gradient(135deg, #FFFFFF 0%, #6EA0B2 100%)",
    text: "#F3FBFF",
    subtitle: "DIAMOND · RESERVE",
    pattern: "brushed",
    holo: "linear-gradient(115deg, transparent 40%, rgba(231,251,255,0.22) 50%, transparent 60%)",
  },
  8000: {
    name: "Emerald",
    // deep emerald with jade foil
    bg: `radial-gradient(ellipse at 20% 0%, #1F8B5A 0%, transparent 55%),
         radial-gradient(ellipse at 100% 100%, #04140C 0%, transparent 55%),
         linear-gradient(135deg, #0A2A1C 0%, #16864E 45%, #04140C 100%)`,
    foil: "#8EEBB4",
    ring: "rgba(142, 235, 180, 0.4)",
    chipBase: "linear-gradient(135deg, #B5F3CF 0%, #0F6B3D 100%)",
    text: "#E8FBF0",
    subtitle: "EMERALD · RESERVE",
    pattern: "guilloche",
    holo: "linear-gradient(115deg, transparent 40%, rgba(142,235,180,0.14) 50%, transparent 60%)",
  },
  10000: {
    name: "Executive VIP",
    // rose gold executive — warm blush with champagne foil
    bg: `radial-gradient(ellipse at 20% 0%, #B76E79 0%, transparent 55%),
         radial-gradient(ellipse at 100% 100%, #1C0A0E 0%, transparent 55%),
         linear-gradient(135deg, #2A1116 0%, #B76E79 45%, #1C0A0E 100%)`,
    foil: "#F2C9CF",
    ring: "rgba(242, 201, 207, 0.45)",
    chipBase: "linear-gradient(135deg, #FFE5EA 0%, #8A4953 100%)",
    text: "#FBECEE",
    subtitle: "EXECUTIVE VIP · RESERVE",
    pattern: "brushed",
    holo: "linear-gradient(115deg, transparent 40%, rgba(242,201,207,0.16) 50%, transparent 60%)",
  },
  20000: {
    name: "Diplomatic",
    // midnight carbon with 24k gold foil — flagship
    bg: `radial-gradient(ellipse at 20% 0%, #241B04 0%, transparent 55%),
         radial-gradient(ellipse at 100% 100%, #000000 0%, transparent 55%),
         linear-gradient(135deg, #000000 0%, #0B0B0B 45%, #000000 100%)`,
    foil: "#FFD700",
    ring: "rgba(255, 215, 0, 0.55)",
    chipBase: "linear-gradient(135deg, #FFE97A 0%, #7A5A00 100%)",
    text: "#F7F2E0",
    subtitle: "DIPLOMATIC · RESERVE",
    pattern: "carbon",
    holo: "linear-gradient(115deg, transparent 40%, rgba(255,215,0,0.14) 50%, transparent 60%)",
  },
};

// --- Deterministic card details per access code (unchanged behaviour) ---
function cardDetailsFor(seed) {
  if (!seed) return { number: "4111 2222 3333 4444", expiry: "12/28", cvv: "489" };
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  const abs = Math.abs(h);
  const g = (n, mod) => (abs * (n + 7) * 0x9E37 + n * 0x85EB) % mod;
  const pad = (v, l) => String(v).padStart(l, "0");
  const num = `4${pad(g(1, 1e3), 3)} ${pad(g(2, 1e4), 4)} ${pad(g(3, 1e4), 4)} ${pad(g(4, 1e4), 4)}`;
  const mm = pad((g(5, 12) + 1), 2);
  const yy = pad(28 + (g(6, 6)), 2);
  const cvv = pad(g(7, 900) + 100, 3);
  return { number: num, expiry: `${mm}/${yy}`, cvv };
}

// --- Reusable SVG defs (patterns per tier) ---
function CardDefs({ id, pattern, foil }) {
  return (
    <defs>
      {pattern === "carbon" && (
        <pattern id={`p-${id}`} width="6" height="6" patternUnits="userSpaceOnUse">
          <rect width="6" height="6" fill="rgba(255,255,255,0.02)" />
          <path d="M0 3 L3 0 L6 3 L3 6 Z" fill="rgba(255,255,255,0.04)" />
        </pattern>
      )}
      {pattern === "brushed" && (
        <pattern id={`p-${id}`} width="1" height="3" patternUnits="userSpaceOnUse">
          <rect width="1" height="3" fill="rgba(255,255,255,0.02)" />
          <rect y="1" width="1" height="1" fill="rgba(255,255,255,0.05)" />
        </pattern>
      )}
      {pattern === "guilloche" && (
        <pattern id={`p-${id}`} width="18" height="18" patternUnits="userSpaceOnUse">
          <path d="M0 9 Q4.5 0 9 9 T18 9" fill="none" stroke="rgba(255,255,255,0.045)" strokeWidth="0.5" />
          <path d="M0 9 Q4.5 18 9 9 T18 9" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
        </pattern>
      )}
      <linearGradient id={`foil-${id}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={foil} stopOpacity="0.9" />
        <stop offset="50%" stopColor="#fff" stopOpacity="0.35" />
        <stop offset="100%" stopColor={foil} stopOpacity="0.9" />
      </linearGradient>
    </defs>
  );
}

// EMV chip: metallic square with etched contact pads
function Chip({ id, base }) {
  return (
    <div
      className="relative shrink-0 rounded-md shadow-inner"
      style={{
        width: 44,
        height: 34,
        background: base,
        boxShadow: "inset 0 0 4px rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.4)",
      }}
    >
      <svg viewBox="0 0 44 34" className="absolute inset-0 h-full w-full" aria-hidden>
        {/* four contact pads */}
        <rect x="4" y="4" width="16" height="9" rx="1" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="0.6" />
        <rect x="24" y="4" width="16" height="9" rx="1" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="0.6" />
        <rect x="4" y="21" width="16" height="9" rx="1" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="0.6" />
        <rect x="24" y="21" width="16" height="9" rx="1" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="0.6" />
        {/* central bus lines */}
        <line x1="22" y1="0" x2="22" y2="34" stroke="rgba(0,0,0,0.3)" strokeWidth="0.6" />
        <line x1="0" y1="17" x2="44" y2="17" stroke="rgba(0,0,0,0.3)" strokeWidth="0.6" />
      </svg>
    </div>
  );
}

// Contactless icon (arcs)
function Contactless({ color, size = 22 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      <g fill="none" stroke={color} strokeLinecap="round" strokeWidth="1.6" transform="rotate(-90 12 12)">
        <path d="M6 6 A9 9 0 0 1 6 18" opacity="0.35" />
        <path d="M9 8 A6.5 6.5 0 0 1 9 16" opacity="0.6" />
        <path d="M12 10 A4 4 0 0 1 12 14" opacity="0.85" />
      </g>
    </svg>
  );
}

// Stylised Visa wordmark (SVG, not text)
function VisaMark({ color = "#ffffff" }) {
  return (
    <svg viewBox="0 0 64 20" width="52" height="16" aria-label="Visa">
      <text
        x="0"
        y="17"
        fontFamily="'Georgia', 'Times New Roman', serif"
        fontWeight="900"
        fontStyle="italic"
        fontSize="20"
        letterSpacing="-1"
        fill={color}
      >
        VISA
      </text>
    </svg>
  );
}

export default function VirtualCard({ clientName, balance, tier = 100, accessCode, preview = null }) {
  const [showCVV, setShowCVV] = useState(false);
  const [copied, setCopied] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const style = TIER_STYLES[tier] || TIER_STYLES[100];
  const details = cardDetailsFor(accessCode);
  const number = preview ? "•••• •••• •••• ••••" : details.number;
  const { expiry, cvv } = details;
  const uid = preview ? `pv-${tier}` : String(tier);

  const handleCopy = async (e) => {
    e?.stopPropagation?.();
    try {
      await navigator.clipboard.writeText(number.replace(/\s/g, ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="space-y-4 font-sans text-white" data-testid={preview ? `virtual-card-preview-${tier}` : "virtual-card"}>
      {!preview && <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">My virtual card</p>
        <span
          className="rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.28em]"
          style={{ borderColor: style.ring, color: style.foil, background: "rgba(255,255,255,0.02)" }}
          data-testid="card-tier-badge"
        >
          {style.name} · Reserve
        </span>
      </div>}

      {/* Flip stage — perspective wrapper */}
      <div
        style={{ perspective: "1400px", aspectRatio: "1.586 / 1" }}
        className={preview ? "" : "cursor-pointer"}
        onClick={() => !preview && setFlipped((v) => !v)}
        data-testid="card-flip-stage"
      >
        <div
          className="relative h-full w-full transition-transform duration-[900ms]"
          style={{
            transformStyle: "preserve-3d",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          {/* ── FRONT ── */}
          <div
            className="absolute inset-0 overflow-hidden rounded-[22px] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.75)]"
            style={{
              background: style.bg,
              border: `1px solid ${style.ring}`,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
            }}
          >
            {/* Pattern overlay */}
            <svg viewBox="0 0 400 252" className="absolute inset-0 h-full w-full" preserveAspectRatio="none" aria-hidden>
              <CardDefs id={uid} pattern={style.pattern} foil={style.foil} />
              <rect width="400" height="252" fill={`url(#p-${uid})`} />
              <circle cx="360" cy="220" r="140" fill={style.foil} opacity="0.05" />
              <circle cx="30" cy="30" r="80" fill="#ffffff" opacity="0.03" />
            </svg>
            {/* Holographic band */}
            <div className="pointer-events-none absolute inset-0" style={{ background: style.holo, mixBlendMode: "screen" }} />

            <div className="relative flex h-full flex-col justify-between p-6 sm:p-7">
              <div className="flex items-start justify-between">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span
                      className="font-serif text-[22px] leading-none tracking-tight"
                      style={{ color: style.text, textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}
                    >
                      Aureon
                    </span>
                    <span className="mt-0.5 text-[9px] font-light tracking-[0.3em]" style={{ color: `${style.text}90` }}>
                      GLOBAL MARKETS
                    </span>
                  </div>
                  <span className="mt-1 text-[9px] font-medium tracking-[0.35em]" style={{ color: style.foil, opacity: 0.85 }}>
                    {style.subtitle}
                  </span>
                </div>
                <VisaMark color={style.text} />
              </div>

              <div className="flex items-center gap-3">
                <Chip id={uid} base={style.chipBase} />
                <Contactless color={style.text} />
                <div className="ml-auto text-right">
                  <p className="text-[8.5px] font-medium uppercase tracking-[0.28em]" style={{ color: `${style.text}80` }}>
                    {preview ? "Entry tier" : "Available balance"}
                  </p>
                  <p
                    className="font-tabular text-xl font-semibold tracking-tight sm:text-2xl"
                    style={{ color: style.text, textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}
                    data-testid={preview ? `card-preview-value-${tier}` : "card-balance"}
                  >
                    $
                    {Number(preview ? tier : balance || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    <span className="ml-1 text-[10px] font-light tracking-widest" style={{ color: `${style.text}70` }}>
                      USD
                    </span>
                  </p>
                </div>
              </div>

              <p
                className="font-tabular text-[15px] tracking-[0.35em] sm:text-[17px]"
                style={{ color: style.text, textShadow: "0 1px 0 rgba(255,255,255,0.15), 0 -1px 0 rgba(0,0,0,0.55)" }}
                data-testid="card-number"
              >
                {number}
              </p>

              <div className="flex items-end justify-between">
                <div>
                  <p className="text-[8.5px] uppercase tracking-[0.28em]" style={{ color: `${style.text}70` }}>
                    Cardholder
                  </p>
                  <p className="text-[13px] font-semibold uppercase tracking-[0.15em]" style={{ color: style.text }} data-testid="card-holder">
                    {clientName || "Valued client"}
                  </p>
                </div>
                <div className="flex gap-5">
                  <div className="text-center">
                    <p className="text-[8.5px] uppercase tracking-[0.28em]" style={{ color: `${style.text}70` }}>
                      Expires
                    </p>
                    <p className="font-tabular text-[13px] font-semibold" style={{ color: style.text }}>
                      {expiry}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[8.5px] uppercase tracking-[0.28em]" style={{ color: `${style.text}70` }}>
                      CVV
                    </p>
                    <p className="font-tabular text-[13px] font-semibold" style={{ color: style.text }} data-testid="card-cvv">
                      {showCVV ? cvv : "•••"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="pointer-events-none absolute -left-16 -top-16 h-40 w-40 rotate-[25deg]"
              style={{ background: `radial-gradient(circle, ${style.foil} 0%, transparent 65%)`, opacity: 0.18, filter: "blur(2px)" }}
            />
            {preview === "locked" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/55 backdrop-blur-[1px]" data-testid={`card-preview-state-${tier}`}>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/60 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] text-white/90">
                  <Lock className="h-3.5 w-3.5" /> Locked
                </span>
              </div>
            )}
            {preview === "achieved" && (
              <span className="absolute bottom-3 right-4 inline-flex items-center gap-1 rounded-full border border-white/30 bg-black/50 px-2.5 py-0.5 text-[9px] uppercase tracking-[0.3em] text-white/90" data-testid={`card-preview-state-${tier}`}>
                <Check className="h-3 w-3" /> Achieved
              </span>
            )}
            {/* subtle "flip" hint */}
            {!preview && <span
              className="absolute bottom-3 right-4 text-[8px] uppercase tracking-[0.35em]"
              style={{ color: `${style.text}55` }}
            >
              Tap to flip
            </span>}
          </div>

          {/* ── BACK ── */}
          <div
            className="absolute inset-0 overflow-hidden rounded-[22px] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.75)]"
            style={{
              background: style.bg,
              border: `1px solid ${style.ring}`,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            {/* Same pattern base */}
            <svg viewBox="0 0 400 252" className="absolute inset-0 h-full w-full" preserveAspectRatio="none" aria-hidden>
              <CardDefs id={`b-${uid}`} pattern={style.pattern} foil={style.foil} />
              <rect width="400" height="252" fill={`url(#p-b-${uid})`} />
            </svg>
            <div className="pointer-events-none absolute inset-0" style={{ background: style.holo, mixBlendMode: "screen" }} />

            <div className="relative flex h-full flex-col p-5 sm:p-6">
              {/* Magnetic stripe */}
              <div
                className="mt-2 h-10 w-full sm:h-11"
                style={{
                  background: "linear-gradient(180deg, #050505 0%, #1a1a1a 40%, #050505 100%)",
                  boxShadow: "inset 0 0 8px rgba(0,0,0,0.7)",
                }}
              />

              {/* Signature panel + CVV block */}
              <div className="mt-5 flex items-center gap-3">
                <div
                  className="relative flex h-10 flex-1 items-center px-3 sm:h-11"
                  style={{
                    background: "linear-gradient(90deg, #F1EDE1 0%, #E8E1D0 100%)",
                    color: "#1a1a1a",
                  }}
                >
                  <span
                    className="font-serif text-sm italic tracking-wide"
                    style={{ color: "#2b2b2b", fontStyle: "italic" }}
                  >
                    {clientName || "Valued Client"}
                  </span>
                  {/* diagonal security print */}
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, rgba(0,0,0,0.05) 0 6px, transparent 6px 12px)",
                    }}
                  />
                </div>
                <div
                  className="flex h-10 items-center px-3 sm:h-11"
                  style={{
                    background: "linear-gradient(90deg, #F1EDE1 0%, #E8E1D0 100%)",
                    color: "#1a1a1a",
                  }}
                >
                  <span className="font-tabular text-sm tracking-[0.2em]" data-testid="card-back-cvv">
                    {showCVV ? cvv : "•••"}
                  </span>
                </div>
              </div>

              {/* Info block */}
              <div className="mt-auto space-y-1">
                <p className="text-[8.5px] uppercase tracking-[0.3em]" style={{ color: `${style.text}60` }}>
                  Concierge · 24/7
                </p>
                <p className="text-[10px] leading-relaxed" style={{ color: `${style.text}85` }}>
                  This virtual card is issued by Aureon Reserve for the exclusive use of the named cardholder.
                  Present at merchant terminals or securely reveal card-not-present details from the front.
                </p>
                <p className="mt-2 text-[8.5px] uppercase tracking-[0.35em]" style={{ color: style.foil, opacity: 0.85 }}>
                  {style.subtitle}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      {!preview && <div className="flex gap-3 pt-1">
        <button
          data-testid="card-copy-btn"
          onClick={handleCopy}
          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-gold px-4 py-2.5 text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-light"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy number"}
        </button>
        <button
          data-testid="card-cvv-btn"
          onClick={(e) => { e.stopPropagation(); setShowCVV((v) => !v); }}
          className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/15 bg-surface px-4 py-2.5 text-xs uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:border-gold hover:text-gold"
        >
          {showCVV ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {showCVV ? "Hide CVV" : "Reveal CVV"}
        </button>
      </div>}
    </div>
  );
}

export { TIER_STYLES };
